import type { ViewerContext } from "../shared/interfaces";

type ProductShell = "web" | "tablet" | "embed" | "native";

export interface ActiveTimeContext {
    feature: string;
    path: string;
    shell: ProductShell;
    viewerContext?: ViewerContext;
}

export interface ActiveTimeSlice {
    usage_session_id: string;
    feature: string;
    active_seconds: number;
    path: string;
    shell: ProductShell;
    viewer_context?: ViewerContext;
}

interface ForegroundState {
    focused: boolean;
    visible: boolean;
}

interface ActiveTimeTrackerOptions {
    emit: (slice: ActiveTimeSlice) => void;
    flushQueue: (keepalive: boolean) => void;
    createUsageSessionId: () => string;
    now?: () => number;
    flushIntervalMs?: number;
    recentInputMs?: number;
    usageSessionGapMs?: number;
}

const DEFAULT_FLUSH_INTERVAL_MS = 60_000;
const DEFAULT_RECENT_INPUT_MS = 60_000;
const DEFAULT_USAGE_SESSION_GAP_MS = 30 * 60_000;

function contextsEqual(left: ActiveTimeContext, right: ActiveTimeContext): boolean {
    return (
        left.feature === right.feature &&
        left.path === right.path &&
        left.shell === right.shell &&
        left.viewerContext === right.viewerContext
    );
}

function contextWithViewerOverride(
    routeContext: ActiveTimeContext,
    viewerContext: ViewerContext | undefined,
): ActiveTimeContext {
    if (!viewerContext) return routeContext;
    return { ...routeContext, feature: "viewer", viewerContext };
}

export class ForegroundActiveTimeTracker {
    private readonly emit: (slice: ActiveTimeSlice) => void;
    private readonly flushQueue: (keepalive: boolean) => void;
    private readonly createUsageSessionId: () => string;
    private readonly now: () => number;
    private readonly flushIntervalMs: number;
    private readonly recentInputMs: number;
    private readonly usageSessionGapMs: number;
    private readonly maxSliceSeconds: number;
    private routeContext: ActiveTimeContext | null = null;
    private viewerOverrides = new Map<symbol, ViewerContext>();
    private focused = false;
    private visible = false;
    private lastSampleAt: number | null = null;
    private recentInputAt: number | null = null;
    private lastSessionInputAt: number | null = null;
    private usageSessionId: string | null = null;
    private activeMilliseconds = 0;
    private flushTimer: ReturnType<typeof setInterval> | null = null;

    constructor({
        emit,
        flushQueue,
        createUsageSessionId,
        now = Date.now,
        flushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS,
        recentInputMs = DEFAULT_RECENT_INPUT_MS,
        usageSessionGapMs = DEFAULT_USAGE_SESSION_GAP_MS,
    }: ActiveTimeTrackerOptions) {
        this.emit = emit;
        this.flushQueue = flushQueue;
        this.createUsageSessionId = createUsageSessionId;
        this.now = now;
        this.flushIntervalMs = flushIntervalMs;
        this.recentInputMs = recentInputMs;
        this.usageSessionGapMs = usageSessionGapMs;
        this.maxSliceSeconds = Math.max(1, Math.floor(flushIntervalMs / 1_000));
    }

    start(routeContext: ActiveTimeContext, state: ForegroundState): void {
        if (this.flushTimer) return;
        this.routeContext = routeContext;
        this.focused = state.focused;
        this.visible = state.visible;
        this.lastSampleAt = this.now();
        this.flushTimer = setInterval(() => this.flush(), this.flushIntervalMs);
    }

    dispose(): void {
        if (!this.flushTimer) return;
        this.flush(false, true);
        clearInterval(this.flushTimer);
        this.flushTimer = null;
        this.routeContext = null;
        this.lastSampleAt = null;
        this.recentInputAt = null;
        this.lastSessionInputAt = null;
        this.usageSessionId = null;
    }

    noteUserInput(isTrusted: boolean): void {
        if (!isTrusted || !this.focused || !this.visible) return;
        const now = this.now();
        this.sample(now);
        if (
            this.lastSessionInputAt === null ||
            now - this.lastSessionInputAt > this.usageSessionGapMs
        ) {
            this.usageSessionId = this.createUsageSessionId();
        }
        this.recentInputAt = now;
        this.lastSessionInputAt = now;
    }

    setFocused(focused: boolean): void {
        if (this.focused === focused) return;
        if (!focused) {
            this.flush(false, true);
            this.recentInputAt = null;
        }
        this.focused = focused;
        this.lastSampleAt = this.now();
    }

    setVisible(visible: boolean): void {
        if (this.visible === visible) return;
        if (!visible) {
            this.flush(true, true);
            this.recentInputAt = null;
        }
        this.visible = visible;
        this.lastSampleAt = this.now();
    }

    setRouteContext(routeContext: ActiveTimeContext): void {
        const current = this.currentContext();
        const next = contextWithViewerOverride(routeContext, this.lastViewerOverride());
        if (current && contextsEqual(current, next)) {
            this.routeContext = routeContext;
            return;
        }
        this.flush(false, true);
        this.routeContext = routeContext;
        this.lastSampleAt = this.now();
    }

    setViewerOverride(token: symbol, viewerContext: ViewerContext): void {
        const current = this.currentContext();
        const nextOverrides = new Map(this.viewerOverrides);
        nextOverrides.delete(token);
        nextOverrides.set(token, viewerContext);
        const next = this.routeContext
            ? contextWithViewerOverride(
                  this.routeContext,
                  Array.from(nextOverrides.values()).at(-1),
              )
            : null;
        if (current && next && !contextsEqual(current, next)) this.flush(false, true);
        this.viewerOverrides = nextOverrides;
        this.lastSampleAt = this.now();
    }

    clearViewerOverride(token: symbol): void {
        if (!this.viewerOverrides.has(token)) return;
        const current = this.currentContext();
        const nextOverrides = new Map(this.viewerOverrides);
        nextOverrides.delete(token);
        const next = this.routeContext
            ? contextWithViewerOverride(
                  this.routeContext,
                  Array.from(nextOverrides.values()).at(-1),
              )
            : null;
        if (current && next && !contextsEqual(current, next)) this.flush(false, true);
        this.viewerOverrides = nextOverrides;
        this.lastSampleAt = this.now();
    }

    flush(keepalive = false, discardRemainder = false): void {
        this.sample(this.now());
        let remainingSeconds = Math.floor(this.activeMilliseconds / 1_000);
        this.activeMilliseconds -= remainingSeconds * 1_000;
        const context = this.currentContext();
        while (context && this.usageSessionId && remainingSeconds > 0) {
            const activeSeconds = Math.min(remainingSeconds, this.maxSliceSeconds);
            this.emit({
                usage_session_id: this.usageSessionId,
                feature: context.feature,
                active_seconds: activeSeconds,
                path: context.path,
                shell: context.shell,
                ...(context.viewerContext ? { viewer_context: context.viewerContext } : {}),
            });
            remainingSeconds -= activeSeconds;
        }
        if (discardRemainder) this.activeMilliseconds = 0;
        if (keepalive) this.flushQueue(true);
    }

    private sample(now: number): void {
        const start = this.lastSampleAt;
        this.lastSampleAt = now;
        if (
            start === null ||
            now <= start ||
            !this.visible ||
            !this.focused ||
            this.recentInputAt === null ||
            !this.usageSessionId
        ) {
            return;
        }
        const activeUntil = this.recentInputAt + this.recentInputMs;
        this.activeMilliseconds += Math.max(0, Math.min(now, activeUntil) - start);
    }

    private currentContext(): ActiveTimeContext | null {
        if (!this.routeContext) return null;
        return contextWithViewerOverride(this.routeContext, this.lastViewerOverride());
    }

    private lastViewerOverride(): ViewerContext | undefined {
        return Array.from(this.viewerOverrides.values()).at(-1);
    }
}

export function resolveActiveTimeFeature(pathname: string): string {
    if (pathname.startsWith("/doc/")) return "viewer";
    if (pathname === "/c" || pathname.startsWith("/c/")) return "marvin";
    if (pathname.startsWith("/w/search")) return "search";
    // Marvin Explore keeps the workbench bucket across its rename so the series stays whole.
    if (pathname.startsWith("/w/explore") || pathname.startsWith("/w/workbench")) {
        return "workbench";
    }
    if (pathname.startsWith("/w/rn")) return "revision_narrative";
    if (pathname.startsWith("/w/cr")) return "constructability";
    if (pathname.startsWith("/w/submittal")) return "submittals";
    if (pathname.startsWith("/w/rfi") || pathname.startsWith("/rfi-links")) return "rfi";
    if (pathname.startsWith("/w/control-tower")) return "control_tower";
    if (pathname.startsWith("/table-edit")) return "tables";
    if (pathname.startsWith("/integrations") || pathname.startsWith("/embed")) {
        return "integrations";
    }
    if (pathname.startsWith("/documents") || pathname.startsWith("/sheets/revisions")) {
        return "documents";
    }
    if (pathname.startsWith("/w/rc")) return "tools";
    if (pathname.startsWith("/admin")) return "admin";
    return "home";
}

export function resolveRouteViewerContext(
    pathname: string,
    search: string,
    state: unknown,
): ViewerContext | undefined {
    if (resolveActiveTimeFeature(pathname) !== "viewer") return undefined;
    const navigationState =
        state && typeof state === "object" ? (state as Record<string, unknown>) : null;
    const isReference =
        /\/c\/[^/]+/.test(pathname) ||
        new URLSearchParams(search).has("ref") ||
        navigationState?.viewerContext === "reference" ||
        Boolean((navigationState?.referenceHighlights as readonly unknown[] | undefined)?.length) ||
        Boolean(navigationState?.connectionDestBounds) ||
        Boolean(navigationState?.connectionSrcBounds);
    return isReference ? "reference" : "direct";
}
